import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import VUSeal from './VUSeal'
import { supabase } from '../supabaseClient'
import { MODULES, ADMIN_EMAIL } from '../constants'
import './AdminDashboard.css'

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [tab, setTab] = useState('cohort')
  const [users, setUsers] = useState([])
  const [progress, setProgress] = useState({})
  const [recordings, setRecordings] = useState({})
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [selectedMod, setSelectedMod] = useState(null)
  const [adminNote, setAdminNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [stats, setStats] = useState({ total: 0, properties: 0, avgProgress: 0 })
  const [recForms, setRecForms] = useState(
    MODULES.reduce((acc, mod, idx) => ({
      ...acc,
      [idx + 1]: { recording_url: '', session_title: '', session_date: '', published: false }
    }), {})
  )
  const [recSaving, setRecSaving] = useState({})
  const [recSaved, setRecSaved] = useState({})

  const loadData = async () => {
    // Load profiles using service role via RPC to bypass RLS
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })

    if (profilesError) {
      console.error('Profiles error:', profilesError)
    }

    const safeProfiles = profiles || []
    setUsers(safeProfiles)

    const { data: allProgress } = await supabase
      .from('module_progress')
      .select('*')
      .eq('curriculum', 'branding')

    const progressMap = {}
    if (allProgress) {
      allProgress.forEach(p => {
        if (!progressMap[p.user_id]) progressMap[p.user_id] = []
        progressMap[p.user_id].push(p)
      })
    }
    setProgress(progressMap)

    const { data: allRecordings } = await supabase
      .from('recordings')
      .select('*')
      .eq('curriculum', 'branding')

    const recMap = {}
    if (allRecordings) {
      allRecordings.forEach(r => { recMap[r.module_number] = r })
      const newForms = { ...recForms }
      allRecordings.forEach(r => {
        newForms[r.module_number] = {
          recording_url: r.recording_url || '',
          session_title: r.session_title || '',
          session_date: r.session_date || '',
          published: r.published || false,
        }
      })
      setRecForms(newForms)
    }
    setRecordings(recMap)

    const uniqueProperties = new Set(safeProfiles.map(p => p.property)).size
    const totalCompleted = Object.values(progressMap).reduce((sum, arr) => sum + arr.filter(p => p.completed_at).length, 0)
    const avgProgress = safeProfiles.length > 0 ? Math.round((totalCompleted / (safeProfiles.length * 8)) * 100) : 0
    setStats({ total: safeProfiles.length, properties: uniqueProperties, avgProgress })
    setLoading(false)
  }

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate('/login'); return }
      await loadData()
    }
    init()
  }, [navigate])

  const handleSignOut = async () => { await supabase.auth.signOut(); navigate('/') }
  const getCompletedCount = (userId) => (progress[userId] || []).filter(p => p.completed_at).length
  const getModProgress = (userId, modNum) => (progress[userId] || []).find(p => p.module_number === modNum)

  const handleRemoveGM = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from the cohort? This cannot be undone.`)) return
    await supabase.from('module_progress').delete().eq('user_id', userId)
    await supabase.from('profiles').delete().eq('id', userId)
    setSelected(null)
    await loadData()
  }

  const handleSelectMod = (mod, idx) => {
    if (!selected) return
    const existing = getModProgress(selected.id, idx + 1)
    setSelectedMod({ mod, idx, existing })
    setAdminNote(existing?.admin_note || '')
  }

  const handleMarkComplete = async (userId, modNum) => {
    const existing = getModProgress(userId, modNum)
    await supabase.from('module_progress').upsert({
      user_id: userId, curriculum: 'branding', module_number: modNum,
      completed_at: existing?.completed_at ? null : new Date().toISOString(),
      gm_note: existing?.gm_note || null, admin_note: existing?.admin_note || null,
    }, { onConflict: 'user_id,curriculum,module_number' })
    await loadData()
    if (selectedMod) {
      const { data: allProgress } = await supabase.from('module_progress').select('*').eq('curriculum', 'branding')
      const progressMap = {}
      if (allProgress) allProgress.forEach(p => { if (!progressMap[p.user_id]) progressMap[p.user_id] = []; progressMap[p.user_id].push(p) })
      const updated = (progressMap[userId] || []).find(p => p.module_number === modNum)
      setSelectedMod(prev => ({ ...prev, existing: updated }))
    }
  }

  const handleSaveAdminNote = async () => {
    if (!selected || !selectedMod) return
    setSaving(true)
    const existing = getModProgress(selected.id, selectedMod.idx + 1)
    await supabase.from('module_progress').upsert({
      user_id: selected.id, curriculum: 'branding', module_number: selectedMod.idx + 1,
      admin_note: adminNote, gm_note: existing?.gm_note || null, completed_at: existing?.completed_at || null,
    }, { onConflict: 'user_id,curriculum,module_number' })
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const handleRecFormChange = (modNum, field, value) => {
    setRecForms(prev => ({ ...prev, [modNum]: { ...prev[modNum], [field]: value } }))
  }

  const handleSaveRecording = async (modNum) => {
    setRecSaving(prev => ({ ...prev, [modNum]: true }))
    const form = recForms[modNum]
    await supabase.from('recordings').upsert({
      curriculum: 'branding', module_number: modNum,
      recording_url: form.recording_url,
      session_title: form.session_title || MODULES[modNum - 1].title,
      session_date: form.session_date || null,
      published: form.published,
    }, { onConflict: 'curriculum,module_number' })
    await loadData()
    setRecSaving(prev => ({ ...prev, [modNum]: false }))
    setRecSaved(prev => ({ ...prev, [modNum]: true }))
    setTimeout(() => setRecSaved(prev => ({ ...prev, [modNum]: false })), 2000)
  }

  const filtered = users.filter(u =>
    (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.property || '').toLowerCase().includes(search.toLowerCase())
  )

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0D3B66', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#A7D8F5', fontFamily: 'Georgia, serif', fontSize: '16px', fontStyle: 'italic' }}>Loading admin dashboard...</p>
    </div>
  )

  return (
    <div className="admin">
      <nav className="admin-nav">
        <span className="admin-nav-logo">VACATIA UNIVERSITY</span>
        <div className="admin-nav-right">
          <span className="admin-nav-badge">Admin</span>
          <button className="admin-nav-link" onClick={() => navigate('/dashboard')}>GM View</button>
          <button className="admin-nav-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      <header className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-seal"><VUSeal size={72} /></div>
          <div>
            <p className="admin-eyebrow">Admin view</p>
            <h1 className="admin-title">Curriculum 01: Branding</h1>
            <p className="admin-sub">Cohort overview - all enrolled GMs</p>
          </div>
        </div>
        <div className="admin-stats">
          <div className="admin-stat"><span className="admin-stat-num">{stats.total}</span><span className="admin-stat-lbl">Enrolled</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{stats.properties}</span><span className="admin-stat-lbl">Properties</span></div>
          <div className="admin-stat"><span className="admin-stat-num">8</span><span className="admin-stat-lbl">Modules</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{stats.avgProgress}%</span><span className="admin-stat-lbl">Avg progress</span></div>
        </div>
      </header>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'cohort' ? 'active' : ''}`} onClick={() => setTab('cohort')}>Cohort & Notes</button>
        <button className={`admin-tab ${tab === 'recordings' ? 'active' : ''}`} onClick={() => setTab('recordings')}>
          Recording Management
          {Object.values(recordings).filter(r => r.recording_url && !r.published).length > 0 && (
            <span className="admin-tab-badge">{Object.values(recordings).filter(r => r.recording_url && !r.published).length}</span>
          )}
        </button>
      </div>

      {tab === 'cohort' && (
        <div className="admin-main">
          <div className="admin-roster">
            <div className="admin-roster-head">
              <h2 className="admin-section-heading">Enrolled GMs</h2>
              <input className="admin-search" type="text" placeholder="Search by name or property..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {filtered.length === 0 ? (
              <div className="admin-empty">
                <p>No GMs enrolled yet.</p>
                <p>Share <strong>university.vacatia.com</strong> to get started.</p>
              </div>
            ) : (
              <div className="admin-list">
                {filtered.map(user => {
                  const completed = getCompletedCount(user.id)
                  const pct = Math.round((completed / 8) * 100)
                  const hasNotes = (progress[user.id] || []).some(p => p.gm_note)
                  return (
                    <div key={user.id} className={`admin-row ${selected?.id === user.id ? 'selected' : ''}`} onClick={() => { setSelected(user); setSelectedMod(null) }}>
                      <div className="admin-row-avatar">{(user.full_name || 'U').charAt(0).toUpperCase()}</div>
                      <div className="admin-row-info">
                        <div className="admin-row-name-row">
                          <p className="admin-row-name">{user.full_name || 'Unnamed'}</p>
                          {hasNotes && <span className="admin-note-dot">●</span>}
                        </div>
                        <p className="admin-row-property">{user.property || 'No property'}</p>
                        <div className="admin-row-bar"><div className="admin-row-fill" style={{ width: `${pct}%` }} /></div>
                      </div>
                      <span className="admin-row-count">{completed}/8</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="admin-detail">
            {!selected ? (
              <div className="admin-detail-empty"><p>Select a GM to manage their progress and leave notes</p></div>
            ) : !selectedMod ? (
              <>
                <div className="admin-detail-head">
                  <div className="admin-detail-avatar">{(selected.full_name || 'U').charAt(0).toUpperCase()}</div>
                  <div>
                    <h3 className="admin-detail-name">{selected.full_name}</h3>
                    <p className="admin-detail-property">{selected.property}</p>
                  </div>
                  <button className="admin-remove-btn" onClick={() => handleRemoveGM(selected.id, selected.full_name)}>Remove from cohort</button>
                </div>
                <p className="admin-detail-label">Modules - click any to manage or leave a note</p>
                <div className="admin-detail-progress">
                  {MODULES.map((mod, idx) => {
                    const modProgress = getModProgress(selected.id, idx + 1)
                    const isComplete = !!modProgress?.completed_at
                    const hasGmNote = !!modProgress?.gm_note
                    return (
                      <div key={mod.number} className="admin-mod-row" onClick={() => handleSelectMod(mod, idx)}>
                        <div className={`admin-mod-dot ${isComplete ? 'complete' : 'pending'}`}>{isComplete ? '✓' : mod.number}</div>
                        <div className="admin-mod-info">
                          <p className="admin-mod-title">{mod.title}</p>
                          {hasGmNote && <p className="admin-mod-note-flag">● Note from GM</p>}
                        </div>
                        <div className="admin-mod-actions">
                          <button className={`admin-mod-toggle ${isComplete ? 'undo' : 'mark'}`} onClick={e => { e.stopPropagation(); handleMarkComplete(selected.id, idx + 1) }}>
                            {isComplete ? 'Undo' : 'Mark complete'}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <>
                <button className="admin-back-btn" onClick={() => setSelectedMod(null)}>← Back to {selected.full_name}</button>
                <div className="admin-mod-detail-head">
                  <p className="admin-detail-label">Module {selectedMod.mod.number}</p>
                  <h3 className="admin-detail-name">{selectedMod.mod.title}</h3>
                </div>
                {selectedMod.existing?.gm_note && (
                  <div className="admin-gm-note-section">
                    <p className="admin-detail-label">Note from {selected.full_name}</p>
                    <div className="admin-gm-note-box"><p>{selectedMod.existing.gm_note}</p></div>
                  </div>
                )}
                <div className="admin-note-section">
                  <p className="admin-detail-label">Your note to {selected.full_name}</p>
                  <textarea className="admin-textarea" placeholder={`Leave a note for ${selected.full_name}...`} value={adminNote} onChange={e => setAdminNote(e.target.value)} rows={4} />
                  <button className="admin-action-btn" onClick={handleSaveAdminNote} disabled={saving}>{saved ? 'Saved.' : saving ? 'Saving...' : 'Save note'}</button>
                </div>
                <div className="admin-mod-toggle-section">
                  <p className="admin-detail-label">Completion</p>
                  <button className={`admin-mod-toggle-big ${selectedMod.existing?.completed_at ? 'undo' : 'mark'}`} onClick={() => handleMarkComplete(selected.id, selectedMod.idx + 1)}>
                    {selectedMod.existing?.completed_at ? 'Mark as incomplete' : 'Mark as complete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'recordings' && (
        <div className="admin-recordings">
          <div className="admin-recordings-intro">
            <p className="admin-recordings-title">Session Recordings</p>
            <p className="admin-recordings-desc">After each session, record with OBS, upload the MP4 to your Google Drive recordings folder, copy the shareable link, and paste it here. Toggle Published when ready for GMs to see it.</p>
          </div>
          <div className="admin-rec-list">
            {MODULES.map((mod, idx) => {
              const modNum = idx + 1
              const form = recForms[modNum]
              const existing = recordings[modNum]
              const isSaving = recSaving[modNum]
              const isSaved = recSaved[modNum]
              return (
                <div key={modNum} className={`admin-rec-card ${existing?.published ? 'published' : ''}`}>
                  <div className="admin-rec-head">
                    <div className="admin-rec-num">{mod.number}</div>
                    <div className="admin-rec-meta">
                      <p className="admin-rec-module-title">{mod.title}</p>
                      {existing?.published
                        ? <span className="admin-rec-status live">● Live to GMs</span>
                        : existing?.recording_url
                        ? <span className="admin-rec-status draft">○ Draft - not published</span>
                        : <span className="admin-rec-status empty">No recording yet</span>
                      }
                    </div>
                  </div>
                  <div className="admin-rec-fields">
                    <div className="admin-rec-field">
                      <label className="admin-rec-label">Google Drive recording link</label>
                      <input className="admin-rec-input" type="url" placeholder="Paste Google Drive shareable link here..." value={form.recording_url} onChange={e => handleRecFormChange(modNum, 'recording_url', e.target.value)} />
                    </div>
                    <div className="admin-rec-row">
                      <div className="admin-rec-field">
                        <label className="admin-rec-label">Session title (optional)</label>
                        <input className="admin-rec-input" type="text" placeholder={mod.title} value={form.session_title} onChange={e => handleRecFormChange(modNum, 'session_title', e.target.value)} />
                      </div>
                      <div className="admin-rec-field">
                        <label className="admin-rec-label">Session date</label>
                        <input className="admin-rec-input" type="date" value={form.session_date} onChange={e => handleRecFormChange(modNum, 'session_date', e.target.value)} />
                      </div>
                    </div>
                    <div className="admin-rec-footer">
                      <label className="admin-rec-toggle-wrap">
                        <input type="checkbox" checked={form.published} onChange={e => handleRecFormChange(modNum, 'published', e.target.checked)} className="admin-rec-checkbox" />
                        <span className="admin-rec-toggle-label">Publish to GMs</span>
                      </label>
                      <button className="admin-rec-save" onClick={() => handleSaveRecording(modNum)} disabled={isSaving || !form.recording_url}>
                        {isSaved ? 'Saved.' : isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <footer className="admin-footer">
        <p>Vacatia University &nbsp;·&nbsp; Admin View &nbsp;·&nbsp; <em>Leave better than you arrived.</em></p>
      </footer>
    </div>
  )
}
